import { ReactElement, useRef } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { ChartSeriesType } from '@common/api';
import { Button } from '@components/button/Button';
import {
    BarChartTile,
    LineChartTile,
    AreaChartTile,
    PinChartTile,
    StackedBarChartTile,
    StackedAreaChartTile,
    WaterfallChartTile,
    IndicatorChartTile,
} from '@components/icons/FarseerIcons';
import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';

export type SeriesTypePickerProps = {
    value: ChartSeriesType;
    disabled?: boolean;
    onChange?: (type: ChartSeriesType) => void;
};

const SERIES_ICONS = {
    [ChartSeriesType.BarChart]: <BarChartTile />,
    [ChartSeriesType.PinChart]: <PinChartTile />,
    [ChartSeriesType.StackedBarChart]: <StackedBarChartTile />,
    [ChartSeriesType.WaterfallChart]: <WaterfallChartTile />,
    [ChartSeriesType.AreaChart]: <AreaChartTile />,
    [ChartSeriesType.StackedAreaChart]: <StackedAreaChartTile />,
    [ChartSeriesType.LineChart]: <LineChartTile />,
    [ChartSeriesType.IndicatorChart]: <IndicatorChartTile />,
} as Record<ChartSeriesType, ReactElement>;

export const SeriesTypePicker = (props: SeriesTypePickerProps) => {
    const { disabled, value, onChange } = props;

    const { LL } = useI18nContext();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const { menuState, menuHandlers } = useContextMenuState({ click: 'left' });

    const icon = SERIES_ICONS[value];

    return (
        <>
            <Button
                {...menuHandlers}
                icon={icon}
                ref={buttonRef}
                disabled={disabled}
                $type="quaternary"
                $size="small"
            />
            <ContextMenu {...menuState} anchorRef={buttonRef} placement="bottom-start">
                <ContextMenu.Item
                    item={{
                        label: LL.BAR(),
                        icon: () => SERIES_ICONS[ChartSeriesType.BarChart],
                        action: () => onChange?.(ChartSeriesType.BarChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.STACKED_BAR(),
                        icon: () => SERIES_ICONS[ChartSeriesType.StackedBarChart],
                        action: () => onChange?.(ChartSeriesType.StackedBarChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.WATERFALL(),
                        icon: () => SERIES_ICONS[ChartSeriesType.WaterfallChart],
                        action: () => onChange?.(ChartSeriesType.WaterfallChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.PIN(),
                        icon: () => SERIES_ICONS[ChartSeriesType.PinChart],
                        action: () => onChange?.(ChartSeriesType.PinChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.AREA(),
                        icon: () => SERIES_ICONS[ChartSeriesType.AreaChart],
                        action: () => onChange?.(ChartSeriesType.AreaChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.STACKED_AREA(),
                        icon: () => SERIES_ICONS[ChartSeriesType.StackedAreaChart],
                        action: () => onChange?.(ChartSeriesType.StackedAreaChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.LINE(),
                        icon: () => SERIES_ICONS[ChartSeriesType.LineChart],
                        action: () => onChange?.(ChartSeriesType.LineChart),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.INDICATOR(),
                        icon: () => SERIES_ICONS[ChartSeriesType.IndicatorChart],
                        action: () => onChange?.(ChartSeriesType.IndicatorChart),
                    }}
                />
            </ContextMenu>
        </>
    );
};
